import { type PropsWithChildren, type ReactNode } from 'react';
import { Tooltip } from 'react-tooltip';
import { Leva, useControls } from 'leva';
import { mix, rgba } from 'polished';
import styled, { ThemeProvider, useTheme } from 'styled-components';

import { Footer } from '@/components/global/Footer';
import { GlobalStyle } from '@/components/v2/GlobalStyle';
import { ContentMarginLine, GridRule } from '@/components/v2/GridLines';
import { Header } from '@/components/v2/Header';
import { HeaderUIProvider } from '@/components/v2/Header/HeaderUIProvider';
import { UtilityCss } from '@/components/v2/UtilityCss';
import { styles } from '@/components/v2/UtilityCss/Text';
import { useHasMounted } from '@/hooks/useHasMounted';
import { base, shades } from '@/utils/theme/palette';

import { FontLinks } from './FontLinks';
import { LayoutGrid, LayoutSubgrid } from './LayoutGrid';

const PaperTexture = styled(LayoutSubgrid)`
  background-image: ${({ theme }) => theme.textures.noise.img};
  background-repeat: repeat;
  background-size: ${({ theme }) => theme.textures.noise.size};
  mix-blend-mode: ${({ theme }) => theme.textures.noise.mixBlendMode};
  opacity: ${({ theme }) => theme.textures.noise.opacity};
  pointer-events: none;
`;

export default function Page({
  children,
  seo
}: PropsWithChildren<{ seo: ReactNode }>) {
  const hasMounted = useHasMounted();
  const theme = useTheme();
  const noiseTexture = useControls(
    'Paper Texture',
    {
      opacity: {
        value: theme.textures.noise.opacity,
        min: 0,
        max: 1
      },
      mixBlendMode: {
        value: theme.textures.noise.mixBlendMode,
        options: {
          normal: 'normal',
          multiply: 'multiply',
          screen: 'screen',
          overlay: 'overlay',
          darken: 'darken',
          lighten: 'lighten',
          'color-dodge': 'color-dodge',
          'color-burn': 'color-burn',
          'hard-light': 'hard-light',
          'soft-light': 'soft-light',
          difference: 'difference',
          exclusion: 'exclusion',
          hue: 'hue',
          saturation: 'saturation',
          color: 'color',
          luminosity: 'luminosity',
          'plus-darker': 'plus-darker',
          'plus-lighter': 'plus-lighter'
        }
      }
    },
    { collapsed: true }
  );
  const pinkScale = useControls(
    'Pink Scale',
    Object.entries(shades).reduce(
      (acc, [key, val]) => {
        acc[`${key}`] = { value: val, min: 0, max: 1 };
        return acc;
      },
      {} as Record<string, { max: number; min: number; value: number }>
    ),
    { collapsed: true }
  );

  type ValueRecordToColorRecord<
    P extends string,
    R extends Record<string | number, string | number>
  > = {
    [K in keyof R as K extends string ? `${P}${K}` : never]: string;
  };

  const fg = Object.entries(pinkScale).reduce(
    (acc, [key, val]) => {
      acc[`fg${key}`] = mix(val, base.roseQuartz, base.obsidian);
      return acc;
    },
    {} as Record<string, string>
  ) as ValueRecordToColorRecord<'fg', typeof shades>;

  const fgA = Object.entries(pinkScale).reduce(
    (acc, [key, val]) => {
      acc[`fgA${key}`] = rgba(base.roseQuartz, val);
      return acc;
    },
    {} as Record<string, string>
  ) as ValueRecordToColorRecord<'fgA', typeof shades>;

  return (
    <>
      <ThemeProvider
        // TODO rip this out once we settle on final colors
        // @ts-expect-error we're gonna rip this out later
        theme={theme => ({
          ...theme!,
          palette: {
            ...theme?.palette,
            ...fg,
            ...fgA
          },
          textures: { noise: { ...theme?.textures.noise, ...noiseTexture } }
        })}>
        <FontLinks />
        <GlobalStyle />
        <UtilityCss />
        {seo}
        <HeaderUIProvider>
          <Leva hidden />
          <LayoutGrid className={styles.base}>
            <a className="sr-only" href="#main">
              Skip to main content
            </a>

            <PaperTexture data-pile />

            <LayoutSubgrid data-pile>
              <Header />

              <LayoutSubgrid as="main" id="main">
                {children}
              </LayoutSubgrid>

              <LayoutSubgrid>
                <ContentMarginLine data-bottom data-top />
                <Footer v2Colors />
                <ContentMarginLine data-bottom data-right data-top />
              </LayoutSubgrid>

              <LayoutSubgrid style={{ height: '2rem' }}>
                <ContentMarginLine data-minor />
                <GridRule $column={4} />
                <GridRule $column={10} />
                <ContentMarginLine data-minor data-right />
              </LayoutSubgrid>
            </LayoutSubgrid>
          </LayoutGrid>

          {hasMounted ? (
            <Tooltip className="toolTip" clickable={true} id="tooltip" />
          ) : null}
        </HeaderUIProvider>
      </ThemeProvider>
    </>
  );
}
